// Vue Core
import Vue from "vue";
import VuePageTransition from "vue-page-transition";
import VueGtag from "vue-gtag";
import VueSanitize from "vue-sanitize";
import App from "./App.vue";

import { PRE_LOGIN_LAYOUT_NAMESPACE, UPDATE_BACKGROUND_IMAGE_VISIBILITY } from "store/modules/pre-login-layout";
import { AUTH_NAMESPACE, REFRESH_TOKEN } from "store/modules/auth";

import "@mdi/font/css/materialdesignicons.css";
import "animate.css/animate.css";
import "assets/styles/app.scss";
import "primevue/resources/themes/saga-blue/theme.css";
import "primevue/resources/primevue.min.css";
import "primeicons/primeicons.css";
import "./registerServiceWorker";

import { AxiosPlugin } from "plugins/axios-plugin";
import vuetify from "plugins/vuetify-plugin";
import dayJsPlugin from "plugins/dayjs-plugin";
import PrimeVue from "primevue/config";
import routerFactory from "router";
import store from "store";
import configurations from "commons/configurations";
import moment from "moment";
import VueSweetAlert from "vue-sweetalert";

// Componentes
import AtivoSelectComponent from "~/components/AtivoSelectComponent.vue";
import AtivoLabelComponent from "~/components/AtivoLabelComponent.vue";
import BreadcrumbsComponent from "~/components/BreadcrumbsComponent.vue";
import ButtonsComponent from "~/components/ButtonsComponent.vue";
import DatePickerComponent from "~/components/DatePickerComponent.vue";
import ColorPickerComponent from "~/components/ColorPickerComponent.vue";
import EditorHTMLComponent from "~/components/EditorHTMLComponent.vue";
import PreviewFileComponent from "~/components/PreviewFileComponent.vue";
import RecaptchaComponent from "~/components/RecaptchaComponent.vue";
import SwitchButtonComponent from "~/components/SwitchButtonComponent.vue";
import TimePickerComponent from "~/components/TimePickerComponent.vue";
import TooltipComponent from "~/components/TooltipComponent.vue";
import UploadFileComponent from "~/components/UploadFileComponent.vue";
import GreCidadeComponent from "~/components/GreCidadeComponent.vue";
import RequiredFieldsWarningComponent from "./components/RequiredFieldsWarningComponent.vue";
import SubmitButtonComponent from "./components/SubmitButtonComponent.vue";
import CalendarioComponent from "./components/CalendarioComponent.vue";


// Mixins
import PageLoadingMixin from "~/mixins/page-loading.mixin";
import NotificationMixin from "~/mixins/notification.mixin";
import ThemeMixin from "~/mixins/theme.mixin";
import ValidationsMixin from "~/mixins/validations.mixin";
import UtilMixin from "~/mixins/util.mixin";
import MenuMixin from "~/mixins/menu.mixin";
import DataTableMixin from "~/mixins/datatables.mixin";

// Filters
import CapitalizeFilter from "~/filters/capitalize.filter";
import CurrencyFilter from "~/filters/currency.filter";
import DateFilter from "~/filters/date.filter";

// Services
import LandingPageService from "~/services/landing-page.service";
import AuthService from "~/services/auth.service";

// Directives
import { VueMaskDirective } from "v-mask";
import DownloadFileDirective from "~/directives/download.directive";
import ClearOnSelectDirective from "~/directives/clear-on-select.directive";
import MaxLengthDirective from "~/directives/max-length.directive";
import OnlyNumberDirective from "~/directives/only-number.directive";
import TransformCaseDirective from "~/directives/transform-case.directive";

Vue.config.productionTip = false;
Vue.prototype.$configurations = configurations;
Vue.prototype.$appName = configurations.app.name;
Vue.prototype.$moment = moment;

// Configurando Plugins
Vue.use(VuePageTransition);
Vue.use(VueSanitize);
Vue.use(dayJsPlugin, { locale: "pt-br" });
Vue.use(AxiosPlugin);
Vue.use(VueSweetAlert);
Vue.use(PrimeVue);

// Declarando Mixins Globais
Vue.mixin(PageLoadingMixin);
Vue.mixin(NotificationMixin);
Vue.mixin(ThemeMixin);
Vue.mixin(ValidationsMixin);
Vue.mixin(UtilMixin);
Vue.mixin(MenuMixin);
Vue.mixin(DataTableMixin)

// Services
Vue.mixin(LandingPageService);
Vue.mixin(AuthService);

// Declarando Components Globais
Vue.component("sca-tooltip", TooltipComponent);
Vue.component("sca-buttons", ButtonsComponent);
Vue.component("sca-recaptcha", RecaptchaComponent);
Vue.component("sca-gre-cidade", GreCidadeComponent);
Vue.component("sca-upload-file", UploadFileComponent);
Vue.component("sca-date-picker", DatePickerComponent);
Vue.component("sca-time-picker", TimePickerComponent);
Vue.component("sca-label-ativo", AtivoLabelComponent);
Vue.component("sca-editor-html", EditorHTMLComponent);
Vue.component("sca-breadcrumbs", BreadcrumbsComponent);
Vue.component("sca-color-picker", ColorPickerComponent);
Vue.component("sca-preview-file", PreviewFileComponent);
Vue.component("sca-select-ativo", AtivoSelectComponent);
Vue.component("sca-switch-button", SwitchButtonComponent);
Vue.component("sca-submit-button", SubmitButtonComponent);
Vue.component("sca-calendario", CalendarioComponent);
Vue.component("sca-required-fields", RequiredFieldsWarningComponent);
// Declarando Diretivas Globais
Vue.directive("mask", VueMaskDirective);
Vue.directive("download", DownloadFileDirective);
Vue.directive("clear-on-select", ClearOnSelectDirective);
Vue.directive("max-length", MaxLengthDirective);
Vue.directive("only-number", OnlyNumberDirective);
Vue.directive("transform-case", TransformCaseDirective);

// Declarando Filters Globais
Vue.filter("capitalize", CapitalizeFilter);
Vue.filter("currency", CurrencyFilter);
Vue.filter("date", DateFilter);

const router = routerFactory(store);

if (configurations.google.id) {
  Vue.use(
    VueGtag,
    {
      config: { id: configurations.google.id },
      appName: configurations.app.name,
      pageTrackerScreenviewEnabled: true,
      pageTrackerUseFullPath: true,
    },
    router
  );
}

new Vue({
  data: () => ({
    loading: {
      visible: true,
      content: undefined,
      progress: undefined,
      opacity: undefined,
    },
    notification: {
      visible: undefined,
      timeout: undefined,
      color: undefined,
      light: undefined,
      centered: undefined,
      absolute: undefined,
      rounded: undefined,
      top: true,
      right: true,
      bottom: undefined,
      left: undefined,
      content: undefined,
      closable: true,
    },
  }),
  router: router,
  store: store,
  vuetify,
  mounted() {
    this.hidePageLoading();
    this.$store.dispatch(`${AUTH_NAMESPACE}/${REFRESH_TOKEN}`);
  },
  methods: {
    showPageLoading() {
      this.loading.visible = true;
    },
    hidePageLoading() {
      this.loading.visible = false;
    },
    showDefaultBackgroundImage() {
      this.$store.dispatch(`${PRE_LOGIN_LAYOUT_NAMESPACE}/${UPDATE_BACKGROUND_IMAGE_VISIBILITY}`, true);
    },
    hideDefaultBackgroundImage() {
      this.$store.dispatch(`${PRE_LOGIN_LAYOUT_NAMESPACE}/${UPDATE_BACKGROUND_IMAGE_VISIBILITY}`, false);
    },
  },
  render: (h) => h(App),
}).$mount("#app");
